import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  ButtonGroup,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { useListClientServices, useListVisits } from 'hooks/useApiQueries';
import React, { useState } from 'react';
import { ClientVisit } from 'types/client';
import { isVisitLateCancel } from 'utils/functions';
import { HTTP } from 'utils/http';

interface CancelVisitModalProps {
  children: React.ReactElement;
  visit: ClientVisit;
  onCancelSuccess?: () => void;
}

const buttonBorderRadius = `${mondayMindbody?.customization?.buttonBorderRadius}px`;

export function CancelVisitModal({ children, visit, onCancelSuccess }: CancelVisitModalProps) {
  const toast = useToast();
  const { refetch: refetchVisits } = useListVisits() || {};
  const { refetch: refetchClientServices } = useListClientServices();
  const { isOpen, onClose, onOpen } = useDisclosure();
  const [isLoading, setIsLoading] = useState(false);

  const close = () => {
    setIsLoading(false);
    onClose();
  };

  async function onCancelConfirmation() {
    setIsLoading(true);
    try {
      await HTTP.post(`/class/cancel`, {
        VisitId: visit.Id,
        ClassId: visit.ClassId,
        StartDateTime: visit.StartDateTime,
        SiteId: visit.SiteId,
      }).send();

      // Ici, après l'annulation réussie, appelez le callback onCancelSuccess
      if (onCancelSuccess) {
        onCancelSuccess();
      }

      await Promise.all([refetchVisits(), refetchClientServices()]);
      toast({
        title: 'La réservation a été annulée',
        status: 'success',
      });
    } catch (error) {
      toast({
        title: "Une erreur est survenue, la réservation n'a pas été annulée",
        status: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  }

  async function onConfirm(ev: React.FormEvent<HTMLElement>) {
    ev.preventDefault();

    setIsLoading(true);
    try {
      await onCancelConfirmation();
      close();
    } catch (error) {
      toast({
        title: "Une erreur est survenue, la réservation n'a pas été annulée",
        status: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  }

  const isLateCancel = isVisitLateCancel(visit);

  return (
    <Box>
      {React.cloneElement(children, { onClick: onOpen })}
      <Modal isOpen={isOpen} onClose={onClose} isCentered size="xl">
        <ModalOverlay />
        <ModalContent as="form">
          <ModalHeader>Annuler ma réservation</ModalHeader>
          <ModalBody>
            <Stack gap={3}>
              <Box>Voulez-vous toujours annuler ?</Box>

              {isLateCancel && (
                <Alert status="error" borderRadius="lg">
                  <AlertIcon />
                  <AlertDescription>
                    Veuillez noter qu'en cas d'annulation tardive, votre crédit ne sera pas recrédité.
                  </AlertDescription>
                </Alert>
              )}
            </Stack>
          </ModalBody>
          <ModalFooter pt={0}>
            <ButtonGroup>
              <Button onClick={close} colorScheme="gray" variant="ghost" textTransform={'uppercase'}>
                Pas maintenant
              </Button>
              <Button
                textTransform={'uppercase'}
                isLoading={isLoading}
                colorScheme="red"
                variant="outline"
                onClick={onConfirm}
                borderRadius={buttonBorderRadius}
              >
                Annuler ma réservation
              </Button>
            </ButtonGroup>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
